import React, { useEffect, useState, useRef } from "react";
import { ArrowLeft, Check, Menu, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "../../Components/UI/dialog";
import { Drawer, DrawerContent } from "../../Components/UI/drawer";
import { Button } from "../../Components/UI/button";
import { Input } from "../../Components/UI/input";
import { Label } from "../../Components/UI/label";
import { getMasterDataFromConfig } from "../../Utils/Storage";
import { saveMastersConfigAPI } from "./MasterDataAPI";
import useIsMobile from "../../Hooks/useIsMobile";
import { cn } from "../../Lib/Cn";

const ORDER_STAGES_TID = 2;

const readOrderStages = () => {
  const threadStages = getMasterDataFromConfig("ThreadStages");
  if (!Array.isArray(threadStages)) return [];
  const stages = threadStages.find((t) => Number(t.TID) === ORDER_STAGES_TID)?.Stages || [];
  return stages
    .map((stage, index) => ({
      id: stage?.SID ?? index + 1,
      name: stage?.S ?? "",
      days: stage?.D ?? 1,
      chances: stage?.C ?? 0,
    }))
    .filter((stage) => stage.name);
};

const persistOrderStages = async (nextStages) => {
  await saveMastersConfigAPI("threadStages", "bulkUpdate", {
    tID: ORDER_STAGES_TID,
    stages: nextStages.map((s) => ({
      SID: s.id,
      S: s.name,
      D: s.days ?? 1,
      C: s.chances ?? 0,
    })),
  });
};

const SortableStageRow = ({ stage, disabled, onEdit, onDelete, isSelected, onSelect }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: stage.id,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={() => onSelect && onSelect(isSelected ? '' : stage.name)}
      className={cn(
        "flex items-center justify-between bg-white shadow-sm border sahi-radius px-4 py-2 w-full transition-colors",
        onSelect && "cursor-pointer hover:bg-gray-50/80",
        isSelected ? "border-sahi-primary-color bg-sahi-primary-color/5" : "border-gray-200",
        isDragging ? "relative z-10 opacity-90 shadow-md" : ""
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        disabled={disabled}
        onClick={(e) => e.stopPropagation()}
        aria-label={`Reorder ${stage.name}`}
        className="flex-shrink-0 mr-4 p-0 touch-none cursor-grab active:cursor-grabbing text-gray-400 hover:text-text-color disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Menu className="h-5 w-5" />
      </button>
      <span className={cn("flex-1 text-sm break-all py-2", isSelected ? "font-semibold text-sahi-primary-color" : "text-text-color")}>
        {stage.name}
      </span>
      {isSelected && (
        <span className="mr-2 inline-flex items-center justify-center text-sahi-primary-color">
          <Check className="h-4 w-4" strokeWidth={3} />
        </span>
      )}
      <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onEdit(stage)}
          disabled={disabled}
          aria-label={`Edit ${stage.name}`}
          className="h-8 w-8 sahi-radius transition-colors text-text-color hover:bg-gray-100"
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDelete(stage)}
          disabled={disabled}
          aria-label={`Delete ${stage.name}`}
          className="h-8 w-8 sahi-radius transition-colors text-sahi-red hover:bg-sahi-red-light"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// ── Mobile Drawer View ──────────────────────────────────────────────
const MobileDrawerBody = ({ stages, onAdd, onEdit, onDelete, initialView, onClose, selectedStage, onSelect, open }) => {
  const [view, setView] = useState(initialView);
  const [editingStage, setEditingStage] = useState(null);
  const [value, setValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const nameInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setView(initialView);
      setEditingStage(null);
      setValue("");
    }
  }, [open, initialView]);

  useEffect(() => {
    if (view === 'add' || view === 'edit') {
      const t = setTimeout(() => nameInputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [view]);

  const handleSave = async () => {
    const trimmed = value.trim();
    if (!trimmed) {
      toast.error("Please enter a stage name.");
      return;
    }
    if (trimmed.length > 25) {
      toast.error("Please enter a stage name within 25 characters.");
      return;
    }
    if (
      stages.some(
        (s) => s.name.toLowerCase() === trimmed.toLowerCase() && s.id !== editingStage?.id
      )
    ) {
      toast.error("A stage with the same name already exists.");
      return;
    }

    setIsSaving(true);
    try {
      if (editingStage) {
        await onEdit(editingStage, trimmed);
      } else {
        await onAdd(trimmed);
      }
      setView('list');
      setEditingStage(null);
      setValue("");
    } catch (e) {
      // error handled by caller
    } finally {
      setIsSaving(false);
    }
  };

  if (view === 'add' || view === 'edit') {
    return (
      <>
        <div className="flex items-center justify-between px-4 h-16 shrink-0">
          <div className="flex gap-4 items-center">
            <button
              type="button"
              tabIndex={-1}
              onClick={() => {
                setView('list');
                setEditingStage(null);
                setValue("");
              }}
              className="p-0 w-auto h-auto flex-shrink-0 flex justify-center text-gray-500"
            >
              <ArrowLeft className="h-[24px] w-[24px]" />
            </button>
            <p className="text-lg font-semibold text-gray-900">
              {view === 'edit' ? 'Edit Order Stage' : 'New Order Stage'}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-4 px-4 pb-4 pt-1">
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium text-gray-700">Stage Name</Label>
              <span className="text-[11px] text-sub-text-color">{value.length}/25</span>
            </div>
            <Input
              ref={nameInputRef}
              value={value}
              maxLength={25}
              placeholder="e.g. Packed"
              disabled={isSaving}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !isSaving && handleSave()}
              className="h-10 text-sm"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <Button
              variant="outline"
              onClick={() => {
                setView('list');
                setEditingStage(null);
                setValue("");
              }}
              className="sahi-secondary-button flex-1"
              disabled={isSaving}
            >
              <X className="h-3.5 w-3.5" /> Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!value.trim() || isSaving}
              className="sahi-primary-button flex-1"
            >
              <Check className="h-3.5 w-3.5" strokeWidth={3} />
              {view === 'edit' ? 'Update Stage' : 'Save Stage'}
            </Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between px-4 h-16 shrink-0">
        <div className="flex gap-4 items-center">
          <button
            type="button"
            tabIndex={-1}
            onClick={onClose}
            className="p-0 w-auto h-auto flex-shrink-0 flex justify-center text-gray-500"
          >
            <ArrowLeft className="h-[24px] w-[24px]" />
          </button>
          <p className="text-lg font-semibold text-gray-900">
            {onSelect ? "Select Order Stage" : "Order Stages"}
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setEditingStage(null);
            setValue("");
            setView('add');
          }}
          className="w-auto h-auto p-0 hover:bg-zinc-100 flex items-center justify-center text-gray-500"
        >
          <Plus className="h-[24px] w-[24px]" />
        </button>
      </div>

      <div className="overflow-y-auto custom-scroll flex-1 px-4 py-1">
        {stages.length > 0 ? (
          <div className="flex flex-col">
            {stages.map((stage) => {
              const isSelected = selectedStage && stage.name.toLowerCase() === selectedStage.toLowerCase();
              return (
                <div
                  key={stage.id}
                  onClick={() => {
                    if (onSelect) {
                      onSelect(isSelected ? '' : stage.name);
                      onClose();
                    } else {
                      setEditingStage(stage);
                      setValue(stage.name);
                      setView('edit');
                    }
                  }}
                  className={cn(
                    "flex items-center gap-2.5 py-2.5 min-h-[48px] pl-3 sahi-radius px-2 transition-colors cursor-pointer active:bg-gray-50",
                    isSelected && "bg-white"
                  )}
                >
                  <span className={cn("flex-1 text-sm font-medium truncate", isSelected ? "font-semibold text-text-color" : "text-text-color")}>
                    {stage.name}
                  </span>
                  <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 p-0"
                      onClick={() => {
                        setEditingStage(stage);
                        setValue(stage.name);
                        setView('edit');
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 p-0 text-sahi-red hover:bg-sahi-red-light"
                      onClick={() => onDelete(stage)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <p className="text-sm text-sub-text-color">No order stages yet. Add one below.</p>
          </div>
        )}
      </div>

      <div className="px-4 pb-4 pt-2 border-t border-zinc-100 shrink-0">
        <Button
          className="sahi-primary-button w-full"
          onClick={() => {
            setEditingStage(null);
            setValue("");
            setView('add');
          }}
        >
          <Plus className="h-4 w-4" /> Add Order Stage
        </Button>
      </div>
    </>
  );
};

const useVisualViewportHeight = () => {
  const [height, setHeight] = useState(() => (typeof window !== 'undefined' && window.visualViewport ? window.visualViewport.height : 700));

  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return;
    const handleResize = () => {
      setHeight(window.visualViewport.height);
    };
    window.visualViewport.addEventListener('resize', handleResize);
    return () => window.visualViewport.removeEventListener('resize', handleResize);
  }, []);

  return height;
};

// ── Main OrderStagesDialog ──────────────────────────────────────────
const OrderStagesDialog = ({
  open,
  onClose: externalOnClose,
  onOpenChange,
  initialView = 'list',
  selectedStage = '',
  onSelect,
}) => {
  const onClose = externalOnClose || (() => onOpenChange && onOpenChange(false));
  const [stages, setStages] = useState([]);
  const [showEntry, setShowEntry] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [value, setValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const isMobile = useIsMobile();
  const vvHeight = useVisualViewportHeight();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 3 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (open) {
      setStages(readOrderStages());
      if (initialView === 'add') {
        setShowEntry(true);
        setEditingStage(null);
        setValue("");
      } else {
        setShowEntry(false);
        setEditingStage(null);
        setValue("");
      }
    }
  }, [open, initialView]);

  const openEntry = (stage = null) => {
    setEditingStage(stage);
    setValue(stage?.name || "");
    setShowEntry(true);
  };

  const cancelEntry = () => {
    setEditingStage(null);
    setValue("");
    setShowEntry(false);
  };

  const handleAdd = async (trimmed) => {
    setIsSaving(true);
    try {
      const nextId = stages.length ? Math.max(...stages.map((s) => Number(s.id) || 0)) + 1 : 1;
      const next = [...stages, { id: nextId, name: trimmed, days: 1, chances: 0 }];
      await persistOrderStages(next);
      setStages(next);
      toast.success("Order stage added.");
      if (onSelect) {
        onSelect(trimmed);
        onClose();
      }
    } catch (error) {
      toast.error(error?.message || "Failed to add order stage.");
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = async (stage, trimmed) => {
    setIsSaving(true);
    try {
      const next = stages.map((s) => (s.id === stage.id ? { ...s, name: trimmed } : s));
      await persistOrderStages(next);
      setStages(next);
      toast.success("Order stage updated.");
      if (onSelect && selectedStage && selectedStage.toLowerCase() === stage.name.toLowerCase()) {
        onSelect(trimmed);
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update order stage.");
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    const trimmed = value.trim();
    if (!trimmed) {
      toast.error("Please enter a stage name.");
      return;
    }
    if (trimmed.length > 25) {
      toast.error("Please enter a stage name within 25 characters.");
      return;
    }
    if (
      stages.some(
        (s) => s.name.toLowerCase() === trimmed.toLowerCase() && s.id !== editingStage?.id
      )
    ) {
      toast.error("A stage with the same name already exists.");
      return;
    }

    try {
      if (editingStage) {
        await handleEdit(editingStage, trimmed);
      } else {
        await handleAdd(trimmed);
      }
      cancelEntry();
    } catch (e) {
      // handled
    }
  };

  const handleDelete = async (stage) => {
    setIsSaving(true);
    try {
      const next = stages.filter((s) => s.id !== stage.id);
      await persistOrderStages(next);
      setStages(next);
      toast.success("Order stage removed.");
      if (onSelect && selectedStage && selectedStage.toLowerCase() === stage.name.toLowerCase()) {
        onSelect('');
      }
    } catch (error) {
      toast.error(error?.message || "Failed to remove order stage.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = stages.findIndex((s) => s.id === active.id);
    const newIndex = stages.findIndex((s) => s.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const previous = stages;
    const reordered = arrayMove(stages, oldIndex, newIndex);
    setStages(reordered);

    setIsSaving(true);
    try {
      await persistOrderStages(reordered);
      toast.success("Order stages reordered.");
    } catch (error) {
      setStages(previous);
      toast.error(error?.message || "Failed to reorder order stages.");
    } finally {
      setIsSaving(false);
    }
  };

  // When opened from ThrDetails with onSelect on mobile, render as bottom Drawer
  if (isMobile && onSelect) {
    return (
      <Drawer open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DrawerContent
          className="p-0 [&>div:first-child]:hidden top-auto rounded-t-[22px] overflow-hidden"
          style={{
            maxHeight: `${vvHeight * 0.85}px`,
            height: 'auto',
          }}
        >
          <div className="flex flex-col" style={{ maxHeight: `${vvHeight * 0.85}px` }}>
            <MobileDrawerBody
              stages={stages}
              onAdd={handleAdd}
              onEdit={handleEdit}
              onDelete={handleDelete}
              initialView={initialView}
              onClose={onClose}
              selectedStage={selectedStage}
              onSelect={onSelect}
              open={open}
            />
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  const dialogTitle = showEntry
    ? editingStage
      ? "Edit Order Stage"
      : "Add Order Stage"
    : onSelect
      ? "Select Order Stage"
      : "Order Stages";

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader className="gap-0 px-3 sm:px-4">
          {/* Mobile view */}
          <div className="sm:hidden flex items-center justify-between w-full h-8">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <DialogClose
                tabIndex={-1}
                className="p-0 w-auto h-auto flex-shrink-0 flex justify-center"
                onClick={(e) => {
                  e.preventDefault();
                  if (showEntry) {
                    cancelEntry();
                  } else {
                    onClose();
                  }
                }}
              >
                <ArrowLeft className="h-6 w-6" />
                <span className="sr-only">Back</span>
              </DialogClose>
              <DialogTitle className="text-lg font-medium">
                <div className="truncate">{dialogTitle}</div>
              </DialogTitle>
            </div>
            {!showEntry && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => openEntry()}
                disabled={isSaving}
                className="h-9 w-9 p-0 hover:bg-white/20 flex items-center justify-center"
              >
                <Plus className="plus-icon" />
              </Button>
            )}
          </div>

          {/* Desktop view */}
          <DialogTitle className="hidden sm:block text-lg md:text-xl">
            {dialogTitle}
          </DialogTitle>
          <div className="hidden sm:flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              tabIndex={-1}
              onClick={() => (showEntry ? cancelEntry() : onClose())}
              className="h-9 w-9 p-0 sm:text-text-color sm:hover:bg-gray-200 flex items-center justify-center"
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </Button>
          </div>
        </DialogHeader>

        {showEntry ? (
          /* Entry mode */
          <div className="px-4 pb-4 space-y-3">
            <Input
              autoFocus
              value={value}
              maxLength={25}
              placeholder="e.g. Packed"
              disabled={isSaving}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !isSaving && handleSave()}
              className="h-11 text-sm"
            />
            <div className="flex sm:flex-row gap-3">
              <Button
                onClick={handleSave}
                disabled={isSaving}
                className="sahi-primary-button sm:flex-none sm:min-w-[140px]"
              >
                {editingStage ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                {editingStage ? "Update Stage" : "Add Stage"}
              </Button>
              <Button
                variant="outline"
                onClick={cancelEntry}
                disabled={isSaving}
                className="sahi-secondary-button sm:flex-none sm:min-w-[100px]"
              >
                <X className="w-3 h-3" />
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          /* List mode */
          <>
            <div className="overflow-auto px-4 lg:max-h-[60vh] flex flex-col custom-scroll">
              {stages.length > 0 ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                  autoScroll={{ threshold: { x: 0, y: 0.05 } }}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={stages.map((s) => s.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-4">
                      {stages.map((stage) => {
                        const isSelected = selectedStage && stage.name.toLowerCase() === selectedStage.toLowerCase();
                        return (
                          <SortableStageRow
                            key={stage.id}
                            stage={stage}
                            disabled={isSaving}
                            onEdit={openEntry}
                            onDelete={handleDelete}
                            isSelected={isSelected}
                            onSelect={onSelect ? (name) => {
                              onSelect(name);
                              onClose();
                            } : undefined}
                          />
                        );
                      })}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : (
                <div className="w-fit text-sm text-sub-text-color bg-sahi-white sahi-radius px-5 py-2 border">
                  No order stages found.
                </div>
              )}
            </div>

            <div className="flex sm:flex-row gap-3 px-4 pb-4">
              <Button
                onClick={() => openEntry()}
                disabled={isSaving}
                className="sahi-primary-button sm:flex-none sm:min-w-[140px]"
              >
                <Plus className="h-4 w-4" />
                Add Stage
              </Button>
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isSaving}
                className="sahi-secondary-button sm:flex-none sm:min-w-[100px]"
              >
                <X className="w-3 h-3" />
                Close
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OrderStagesDialog;
